/**
 * @module flightAttendant.service
 */
import { DialVerseApi } from '../app/api'
import AppConfig from '../config'

const FlightAttendantService = DialVerseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Basic CRUD operations
    getFlightAttendants: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/flight-attendants'),
        method: 'GET',
      }),
    }),
    getFlightAttendantById: builder.mutation({
      query: (flightAttendantId) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}`),
        method: 'GET',
      }),
    }),
    getFlightAttendantByEmployeeNumber: builder.mutation({
      query: (employeeNumber) => ({
        url: AppConfig.getUrl(`/flight-attendants/employee/${employeeNumber}`),
        method: 'GET',
      }),
    }),
    createFlightAttendant: builder.mutation({
      query: (flightAttendant) => ({
        url: AppConfig.getUrl('/flight-attendants'),
        method: 'POST',
        body: flightAttendant,
      }),
    }),
    updateFlightAttendant: builder.mutation({
      query: ({ flightAttendantId, ...updates }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}`),
        method: 'PUT',
        body: updates,
      }),
    }),
    deleteFlightAttendant: builder.mutation({
      query: (flightAttendantId) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}`),
        method: 'DELETE',
      }),
    }),

    // Status and profile updates
    updateStatus: builder.mutation({
      query: ({ flightAttendantId, status }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/status`),
        method: 'PATCH',
        body: { status },
      }),
    }),
    updateCertifications: builder.mutation({
      query: ({ flightAttendantId, certifications }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/certifications`),
        method: 'PATCH',
        body: { certifications },
      }),
    }),
    updateContactInfo: builder.mutation({
      query: ({ flightAttendantId, contactInfo }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/contact-info`),
        method: 'PATCH',
        body: { contactInfo },
      }),
    }),
    updateMedicalInfo: builder.mutation({
      query: ({ flightAttendantId, medicalInfo }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/medical-info`),
        method: 'PATCH',
        body: { medicalInfo },
      }),
    }),
    updateUniform: builder.mutation({
      query: ({ flightAttendantId, uniform }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/uniform`),
        method: 'PATCH',
        body: { uniform },
      }),
    }),

    // Language operations
    addLanguage: builder.mutation({
      query: ({ flightAttendantId, language }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/languages`),
        method: 'POST',
        body: { language },
      }),
    }),
    removeLanguage: builder.mutation({
      query: ({ flightAttendantId, language }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/languages`),
        method: 'DELETE',
        body: { language },
      }),
    }),

    // Aircraft type operations
    addAircraftType: builder.mutation({
      query: ({ flightAttendantId, aircraftType }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/aircraft-types`),
        method: 'POST',
        body: { aircraftType },
      }),
    }),
    removeAircraftType: builder.mutation({
      query: ({ flightAttendantId, aircraftType }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/aircraft-types`),
        method: 'DELETE',
        body: { aircraftType },
      }),
    }),

    // Special qualification operations
    addSpecialQualification: builder.mutation({
      query: ({ flightAttendantId, qualification }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/qualifications`),
        method: 'POST',
        body: { qualification },
      }),
    }),
    removeSpecialQualification: builder.mutation({
      query: ({ flightAttendantId, qualification }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/qualifications`),
        method: 'DELETE',
        body: { qualification },
      }),
    }),

    // Schedule operations
    updateAvailability: builder.mutation({
      query: ({ flightAttendantId, availability }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/availability`),
        method: 'PATCH',
        body: { availability },
      }),
    }),
    requestTimeOff: builder.mutation({
      query: ({ flightAttendantId, timeOffRequest }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/time-off`),
        method: 'POST',
        body: { timeOffRequest },
      }),
    }),
    updateTimeOffRequest: builder.mutation({
      query: ({ flightAttendantId, timeOffRequest }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/time-off`),
        method: 'PATCH',
        body: { timeOffRequest },
      }),
    }),

    // Performance operations
    addEvaluation: builder.mutation({
      query: ({ flightAttendantId, evaluation }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/evaluations`),
        method: 'POST',
        body: { evaluation },
      }),
    }),
    addIncident: builder.mutation({
      query: ({ flightAttendantId, incident }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/incidents`),
        method: 'POST',
        body: { incident },
      }),
    }),
    addCommendation: builder.mutation({
      query: ({ flightAttendantId, commendation }) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/commendations`),
        method: 'POST',
        body: { commendation },
      }),
    }),

    // Employment status
    terminate: builder.mutation({
      query: (flightAttendantId) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/terminate`),
        method: 'POST',
      }),
    }),
    reactivate: builder.mutation({
      query: (flightAttendantId) => ({
        url: AppConfig.getUrl(`/flight-attendants/${flightAttendantId}/reactivate`),
        method: 'POST',
      }),
    }),

    // Query operations
    getActiveFlightAttendants: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/flight-attendants/status/active'),
        method: 'GET',
      }),
    }),
    getFlightAttendantsByAirline: builder.mutation({
      query: (airlineId) => ({
        url: AppConfig.getUrl(`/flight-attendants/airline/${airlineId}`),
        method: 'GET',
      }),
    }),
    getFlightAttendantsByAircraft: builder.mutation({
      query: (aircraftId) => ({
        url: AppConfig.getUrl(`/flight-attendants/aircraft/${aircraftId}`),
        method: 'GET',
      }),
    }),
    getFlightAttendantsByBaseAirport: builder.mutation({
      query: (baseAirport) => ({
        url: AppConfig.getUrl(`/flight-attendants/base/${baseAirport}`),
        method: 'GET',
      }),
    }),

    // Compliance and monitoring
    getFlightAttendantsNeedingRecurrentTraining: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/flight-attendants/compliance/recurrent-training'),
        method: 'GET',
      }),
    }),
    getFlightAttendantsWithExpiringMedical: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/flight-attendants/compliance/expiring-medical'),
        method: 'GET',
      }),
    }),
    getFlightAttendantsWithExpiringEmergencyTraining: builder.mutation({
      query: () => ({
        url: AppConfig.getUrl('/flight-attendants/compliance/expiring-emergency-training'),
        method: 'GET',
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetFlightAttendantsMutation,
  useGetFlightAttendantByIdMutation,
  useGetFlightAttendantByEmployeeNumberMutation,
  useCreateFlightAttendantMutation,
  useUpdateFlightAttendantMutation,
  useDeleteFlightAttendantMutation,
  useUpdateStatusMutation,
  useUpdateCertificationsMutation,
  useUpdateContactInfoMutation,
  useUpdateMedicalInfoMutation,
  useUpdateUniformMutation,
  useAddLanguageMutation,
  useRemoveLanguageMutation,
  useAddAircraftTypeMutation,
  useRemoveAircraftTypeMutation,
  useAddSpecialQualificationMutation,
  useRemoveSpecialQualificationMutation,
  useUpdateAvailabilityMutation,
  useRequestTimeOffMutation,
  useUpdateTimeOffRequestMutation,
  useAddEvaluationMutation,
  useAddIncidentMutation,
  useAddCommendationMutation,
  useTerminateMutation,
  useReactivateMutation,
  useGetActiveFlightAttendantsMutation,
  useGetFlightAttendantsByAircraftMutation,
  useGetFlightAttendantsByBaseAirportMutation,
  useGetFlightAttendantsNeedingRecurrentTrainingMutation,
  useGetFlightAttendantsWithExpiringMedicalMutation,
  useGetFlightAttendantsWithExpiringEmergencyTrainingMutation,
  useGetFlightAttendantsByAirlineMutation,
} = FlightAttendantService
